import { useState } from "react";
import {
  Plane,
  Ship,
  ShieldCheck,
  ChevronRight,
  Search,
  ShoppingCart,
  Package,
  TrendingUp,
} from "lucide-react";
import ModalModel from "../Modals/ModalModel";
import TransportExplainModal from "../Modals/TransportExplainModal";
import { formatShippingRate } from "../../utils/pricing";
import usePricing from "../../hooks/usePricing";

/** Délais éditoriaux ; le tarif s'ajoute depuis le barème backend. */
const DELAIS = {
  avion: "5–7 jours A/D",
  bateau: "60–90 jours",
};

const STEPS = [
  {
    icon: Search,
    color: "text-blue-600",
    bg: "bg-blue-50",
    title: "Trouve les bons produits",
    text: "Nous sélectionnons pour toi des produits à fort potentiel commercial et de rentabilité, avec les informations essentielles pour t’aider à faire les bons choix.",
  },
  {
    icon: ShoppingCart,
    color: "text-orange-600",
    bg: "bg-orange-50",
    title: "Commande en toute simplicité",
    text: "Tu choisis tes produits, tu passes tes commandes, et nous prenons en charge leur traitement.",
  },
  {
    icon: Package,
    color: "text-teal-600",
    bg: "bg-teal-50",
    title: "Nous gérons l’importation",
    text: "Réception des marchandises dans notre centre logistique en Chine, consolidation, expédition, gestion des procédures douanières, puis remise au client ou livraison à domicile.",
  },
  {
    icon: TrendingUp,
    color: "text-green-600",
    bg: "bg-green-50",
    title: "Tu te concentres sur ton business",
    text: "À la réception, tu peux te concentrer sur l’essentiel : trouver tes clients, développer tes ventes et faire grandir ton activité.",
  },
];

// Avantages affichés un par un : pause puis glissement vers le suivant (2 items).
// Le 1er est dupliqué en fin de liste pour reboucler sans à-coup.
const AvantagesScroller = ({ items }) => {
  const loop = [...items, items[0]];
  return (
    <div className="ml-[22px] mt-0.5 h-4 overflow-hidden">
      <div className="flex flex-col animate-adv-rotate">
        {loop.map((text, i) => (
          <p key={i} className="h-4 text-xs leading-4 text-gray-500 whitespace-nowrap">
            {text}
          </p>
        ))}
      </div>
    </div>
  );
};

const WhyChoose = () => {
  const [open, setOpen] = useState(false);
  const [transportMode, setTransportMode] = useState(null);

  // Tarifs servis par le backend : le bandeau ne doit jamais annoncer un prix
  // que l'administration a changé en base.
  const { pricing } = usePricing();
  const avantages = {
    avion: [DELAIS.avion, formatShippingRate("aerien", pricing)].filter(Boolean),
    bateau: [DELAIS.bateau, formatShippingRate("maritime", pricing)].filter(Boolean),
  };

  return (
    <>
      <div className="mx-3 mt-3 space-y-2">
        {/* Bloc 1 — modes de livraison Avion / Bateau (cliquable) */}
        <div className="flex items-stretch rounded-xl bg-[#F4F6CE] px-1 py-1 min-[400px]:px-1.5 min-[400px]:py-1.5">
          <button
            type="button"
            onClick={() => setTransportMode("aerien")}
            className="flex-1 min-w-0 text-left rounded-lg px-2 py-1.5 min-[400px]:px-2.5 min-[400px]:py-2 active:bg-white/40 transition-colors"
            aria-label="Voir le détail du transport Avion"
          >
            <div className="flex items-center gap-1.5">
              <Plane className="w-4 h-4 text-blue-600 shrink-0" strokeWidth={2.5} />
              <span className="text-[13px] min-[400px]:text-sm font-bold text-blue-700 truncate">
                Avion Express
              </span>
            </div>
            <AvantagesScroller items={avantages.avion} />
          </button>

          <div className="w-px self-stretch bg-gray-300/70 my-1" />

          <button
            type="button"
            onClick={() => setTransportMode("maritime")}
            className="flex-1 min-w-0 text-left rounded-lg px-2 py-1.5 min-[400px]:px-2.5 min-[400px]:py-2 active:bg-white/40 transition-colors"
            aria-label="Voir le détail du transport Bateau"
          >
            <div className="flex items-center gap-1.5">
              <Ship className="w-4 h-4 text-teal-600 shrink-0" strokeWidth={2.5} />
              <span className="text-[13px] min-[400px]:text-sm font-bold text-teal-700 truncate">
                Bateau
              </span>
            </div>
            <AvantagesScroller items={avantages.bateau} />
          </button>
        </div>

        {/* Bloc 2 — bandeau vert */}
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="w-full bg-green-600 rounded-xl px-3 py-2.5 flex items-center justify-between gap-2 active:opacity-90 transition-opacity"
        >
          <span className="flex items-center gap-1.5 min-w-0">
            <ShieldCheck className="w-4.5 h-4.5 text-white shrink-0" size={18} />
            <span className="text-white text-[13px] min-[400px]:text-[14px] font-bold truncate">
              Pourquoi choisir Chinexpress
            </span>
          </span>
          <span className="flex items-center gap-0.5 text-white text-[13px] shrink-0">
            <span className="hidden min-[430px]:inline">Confidentialité sécurisée</span>
            <ChevronRight className="w-4 h-4" />
          </span>
        </button>
      </div>

      <TransportExplainModal
        mode={transportMode}
        onClose={() => setTransportMode(null)}
      />

      <ModalModel
        active={open}
        onClose={() => setOpen(false)}
        title="Pourquoi choisir Chinexpress"
        icon={<ShieldCheck className="w-5 h-5 text-green-600" />}
      >
        <div className="pb-2">
          <p className="text-sm text-gray-600 leading-relaxed mb-4 px-1 text-center">
            Avec Chinexpress, transforme une idée de business en activité commerciale concrète.
          </p>

          <div className="space-y-2.5">
            {STEPS.map(({ icon: Icon, color, bg, title, text }) => (
              <div
                key={title}
                className="flex items-start gap-3 px-3 py-3 rounded-xl border border-gray-100 bg-gray-50"
              >
                <div
                  className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${bg}`}
                >
                  <Icon className={color} size={20} strokeWidth={2.25} />
                </div>
                <div className="min-w-0 pt-0.5">
                  <p className="text-[14px] font-semibold text-gray-900 leading-tight">
                    {title}
                  </p>
                  <p className="text-[12px] text-gray-500 mt-1 leading-relaxed">
                    {text}
                  </p>
                </div>
              </div>
            ))}
          </div>

          <p className="text-center text-sm font-bold text-green-700 pt-4 pb-1">
            Chinexpress — pense business.
          </p>
        </div>
      </ModalModel>
    </>
  );
};

export default WhyChoose;
